import {
  Divider as FlicketDivider,
  DividerProps,
  LinkText,
  Price,
  Stack,
  SystemProps,
  Text,
  TextProps,
  VStack,
} from "flicket-ui";
import {
  OrderSummaryProvider,
  useOrderSummaryContext,
} from "./context/OrderSummaryContext";
import { ListItem, ListItemProps } from "./components/ListItem";
import { AnimateReveal } from "~components/AnimateReveal/AnimateReveal";
import { ReactNode, useState } from "react";
import { FixedRatioImage } from "~components/common/Image";
import { useOrganization } from "~hooks/useOrganization";
import { PromoCodeInput as PromoCodeInputComponent } from "~features/checkout/standard/components/PromoCodeInput";
import { PromoCodeSummary as PromoCodeSummaryComponent } from "~features/checkout/standard/components/PromoCodeSummary";
import { ActivePromotion } from "~hooks";
import { calculateMultiBuyPricing } from "~components/checkout/order/calculateMultiBuyPricing";

function LineItems() {
  const { lineItems } = useOrderSummaryContext();
  const { organization } = useOrganization();

  return (
    <>
      {lineItems?.map((item) => {
        const showPriceDifference =
          !item.multiBuy && item.price !== item.originalPrice;
        return (
          <>
            <ListItem
              key={item.id}
              title={item.title}
              subtitle={item.subtitle}
              price={item.originalPrice * item.quantity}
              renderPrice={({ price }) => (
                <>
                  <Price price={price} currency={organization.currency} />
                  {showPriceDifference && (
                    <Text variant="small" color="success">
                      <Price
                        price={-((item.originalPrice - price) * item.quantity)}
                        currency={organization.currency}
                      />
                    </Text>
                  )}
                </>
              )}
            />
            {item.multiBuy && (
              <MultibuyDiscount
                key={`${item.id}-${item.multiBuy.promotion?.id}`}
                multiBuy={item.multiBuy}
                originalPrice={item.originalPrice}
              />
            )}
          </>
        );
      })}
    </>
  );
}

function MultibuyDiscount({
  multiBuy,
  originalPrice,
}: {
  multiBuy: ActivePromotion;
  originalPrice: number;
}) {
  const { isLoading } = useOrderSummaryContext();
  const { organization, hasFeature } = useOrganization();

  const hideMultiBuySavings = hasFeature("HideMultiBuySavings");

  if (hideMultiBuySavings) {
    return null;
  }

  const { multiBuySavings } = calculateMultiBuyPricing(multiBuy, originalPrice);

  return (
    <ListItem
      title={"Multibuy promotion applied"}
      price={multiBuySavings}
      isLoading={isLoading}
      renderPrice={({ price }) => (
        <Text variant="regular" color="success">
          <Price price={-price} currency={organization.currency} />
        </Text>
      )}
    />
  );
}

function ListItemInternal(props: ListItemProps & Omit<SystemProps, "title">) {
  return <ListItem {...props} />;
}

function ServiceFeeItems() {
  const { serviceCosts, isLoading } = useOrderSummaryContext();
  return (
    <>
      {serviceCosts.map(({ price, title, subtitle }) => (
        <ListItem
          key={title}
          title={title}
          subtitle={subtitle}
          price={price}
          isLoading={isLoading}
        />
      ))}
    </>
  );
}

export const ServiceFeesToggle = ({ children }: { children: ReactNode }) => {
  const { isLoading, totalServiceCost } = useOrderSummaryContext();
  const [showDetails, setShowDetails] = useState(false);

  return (
    <Stack direction={"vertical"}>
      <ListItem
        title={<LinkText>Service costs</LinkText>}
        onClick={() => setShowDetails(!showDetails)}
        price={!showDetails ? totalServiceCost : undefined}
        isLoading={isLoading}
      />
      <AnimateReveal show={showDetails}>
        <Stack direction={"vertical"} gap={1} mt={1}>
          {children}
        </Stack>
      </AnimateReveal>
    </Stack>
  );
};

function Total(props: Partial<ListItemProps & SystemProps>) {
  const { totals, isLoading } = useOrderSummaryContext();
  return (
    <ListItem
      title={"Total"}
      price={totals?.remainingPayment}
      variant="header.S"
      isLoading={isLoading}
      loaderWidth={80}
      {...props}
    />
  );
}

function OrderSummaryText(props: Partial<ListItemProps>) {
  return <ListItem title={"Order summary"} {...props} />;
}

function Divider(props: DividerProps) {
  return <FlicketDivider {...props} />;
}

function PromoCodeInput() {
  return <PromoCodeInputComponent />;
}

function PromoCodeSummary() {
  return <PromoCodeSummaryComponent />;
}

function TicketProtectionFee(props: Partial<ListItemProps>) {
  const { totals, isLoading } = useOrderSummaryContext();

  return (
    <AnimateReveal initial={true} show={!!totals?.ticketCover}>
      <ListItem
        title={"Ticket protection cover"}
        price={totals?.ticketCover}
        isLoading={isLoading}
        {...props}
      />
    </AnimateReveal>
  );
}

function Credits(props: Partial<ListItemProps>) {
  const { totals, isLoading } = useOrderSummaryContext();

  return (
    <AnimateReveal
      initial={true}
      show={!!totals?.usedCredits && totals.usedCredits > 0}
    >
      <ListItem
        title="Account credits"
        subtitle={`$${
          totals?.remainingCredits?.toFixed(2) ?? "0.00"
        } remaining on your account`}
        price={totals?.usedCredits ? -totals.usedCredits : undefined}
        isLoading={isLoading}
        {...props}
      />
    </AnimateReveal>
  );
}

function PaymentPlanFee(props: Partial<ListItemProps>) {
  const { totals, isLoading } = useOrderSummaryContext();

  return (
    <AnimateReveal
      initial={true}
      show={!!totals?.paymentPlanFee && totals.paymentPlanFee > 0}
    >
      <ListItem
        title="Payment plan fee"
        price={totals?.paymentPlanFee}
        isLoading={isLoading}
        {...props}
      />
    </AnimateReveal>
  );
}

function DueNow(props: Partial<ListItemProps>) {
  const { totals, isLoading, paymentPlanMetadata } = useOrderSummaryContext();

  // Show deposit amount if payment plan is selected, otherwise show full remaining payment
  const dueNow = paymentPlanMetadata?.depositAmount ?? totals?.remainingPayment;

  return (
    <AnimateReveal initial={true} show={!!paymentPlanMetadata?.depositAmount}>
      <ListItem
        title="Due now"
        variant="header.S"
        price={dueNow}
        isLoading={isLoading}
        loaderWidth={80}
        {...props}
      />
    </AnimateReveal>
  );
}

function DueLater(props: Partial<ListItemProps>) {
  const { isLoading, paymentPlanMetadata } = useOrderSummaryContext();
  const { organization } = useOrganization();

  if (
    !paymentPlanMetadata?.installmentAmount ||
    !paymentPlanMetadata?.installmentCount
  ) {
    return null;
  }

  // Calculate due later: installment amount × (number of installments - 1)
  // Subtract 1 because the first payment (deposit) is due now
  const dueLater =
    paymentPlanMetadata.installmentAmount *
    (paymentPlanMetadata.installmentCount - 1);

  const remainingInstallments = paymentPlanMetadata.installmentCount - 1;

  return (
    <AnimateReveal initial={true} show={dueLater > 0}>
      <ListItem
        title="Due later"
        subtitle={
          <span>
            {remainingInstallments} x{" "}
            <Price
              price={paymentPlanMetadata.installmentAmount}
              currency={organization.currency}
            />
          </span>
        }
        price={dueLater}
        isLoading={isLoading}
        loaderWidth={80}
        {...props}
      />
    </AnimateReveal>
  );
}

/**
 * To be completed
 */
function EventImage() {
  const { image, title } = useOrderSummaryContext();

  return (
    <FixedRatioImage
      alt={title}
      aspectRatio="5 / 2"
      image={image}
      width={"100%"}
      overflow="hidden"
      borderRadius={"md"}
      useNearFitCover={true}
    />
  );
}

function EventInfo({
  variant = "regular",
}: {
  variant?: TextProps["variant"];
}) {
  const { subtitle, title } = useOrderSummaryContext();
  return (
    <VStack gap={"1/2"}>
      <Text variant={variant}>{title}</Text>
      <Text variant="small">{subtitle}</Text>
    </VStack>
  );
}

function AllLineItemsAndFees() {
  return (
    <>
      <OrderSummary.LineItems />
      <OrderSummary.TicketProtectionFee />

      <OrderSummary.ServiceFeesToggle>
        <OrderSummary.ServiceFeeItems />
      </OrderSummary.ServiceFeesToggle>

      <OrderSummary.Credits />
      <OrderSummary.PromoCodeSummary />
      <OrderSummary.PaymentPlanFee />
    </>
  );
}

export const OrderSummary = {
  Provider: OrderSummaryProvider,

  ListItem: ListItemInternal,
  OrderSummaryText,
  LineItems,
  AllLineItemsAndFees,
  ServiceFeesToggle,
  ServiceFeeItems,
  Total,
  Divider,
  TicketProtectionFee,
  Credits,
  PaymentPlanFee,
  DueNow,
  DueLater,
  EventImage,
  EventInfo,
  MultibuyDiscount,

  PromoCodeInput,
  PromoCodeSummary,
};
